import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import {
  LayoutDashboard, Settings2, Save, RefreshCw, MessageSquare, UserCheck,
  Activity, X,
  Users, Search, ChevronLeft, ChevronRight, Bot, UserCircle, Clock,
} from 'lucide-react';
import BotKnowledgeBase from './BotKnowledgeBase';

// ── Helpers de formatação ─────────────────────────────────────────────────────

function fmtPhone(p) {
  const d = String(p || '').replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 12) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,8)}-${d.slice(8)}`;
  return p;
}

function fmtTs(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const STAGE_LABEL = {
  new: { label: 'Novo', cls: 'bg-gray-100 text-gray-500' },
  engaged: { label: 'Engajado', cls: 'bg-blue-100 text-blue-700' },
  qualified: { label: 'Qualificado', cls: 'bg-green-100 text-green-700' },
  material_sent: { label: 'Material Enviado', cls: 'bg-purple-100 text-purple-700' },
  visit_scheduled: { label: 'Visita Agendada', cls: 'bg-orange-100 text-orange-700' },
};

// ── Modal de Conversa ─────────────────────────────────────────────────────────

function ModalConversa({ phone, bot, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/automacoes/atendimentos/${bot}/${encodeURIComponent(phone)}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [phone, bot]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [data]);

  const conv = data?.conv;
  const msgs = data?.msgs || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <UserCircle size={22} className="text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 truncate">{conv?.contact_name || 'Lead'}</p>
            <p className="text-xs text-gray-400">{fmtPhone(phone)}</p>
          </div>
          {conv?.stage && (
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${(STAGE_LABEL[conv.stage] || STAGE_LABEL.new).cls}`}>
              {(STAGE_LABEL[conv.stage] || { label: conv.stage }).label}
            </span>
          )}
          {conv?.human_takeover && (
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">Humano</span>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2"><X size={20} /></button>
        </div>

        {/* Mensagens */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
          {loading && <p className="text-center text-gray-400 py-8">Carregando...</p>}
          {!loading && msgs.length === 0 && <p className="text-center text-gray-400 py-8">Nenhuma mensagem registrada.</p>}
          {msgs.map((m, i) => {
            const isIn = m.direction === 'in';
            return (
              <div key={i} className={`flex ${isIn ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[78%] rounded-2xl px-3 py-2 shadow-sm ${isIn ? 'bg-white text-gray-800 rounded-tl-sm' : 'bg-blue-500 text-white rounded-tr-sm'}`}>
                  <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.content}</p>
                  <p className={`text-[10px] mt-1 ${isIn ? 'text-gray-400' : 'text-blue-200'} text-right`}>{fmtTs(m.created_at)}</p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Footer info */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1"><Clock size={11} /> Início: {fmtTs(conv?.created_at)}</span>
          <span className="flex items-center gap-1"><MessageSquare size={11} /> {msgs.length} mensagens</span>
          <span className="ml-auto">{bot === 'sdr' ? 'Iago SDR' : bot === 'bruna' ? 'Bruna SDR' : 'Iago Vendedor'}</span>
        </div>
      </div>
    </div>
  );
}

// ── Aba de Atendimentos ───────────────────────────────────────────────────────

function AtendimentosTab() {
  const [bot, setBot] = useState('vendedor');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalPhone, setModalPhone] = useState(null);
  const PER_PAGE = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/automacoes/atendimentos', { params: { bot, page, limit: PER_PAGE, search } });
      setRows(r.data.data || []);
      setTotal(r.data.total || 0);
    } catch {}
    setLoading(false);
  }, [bot, page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="space-y-4">
      {modalPhone && <ModalConversa phone={modalPhone} bot={bot} onClose={() => setModalPhone(null)} />}

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {[['vendedor', 'Iago Vendedor'], ['sdr', 'Iago SDR'], ['bruna', 'Bruna SDR']].map(([k, l]) => (
            <button key={k} onClick={() => { setBot(k); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${bot === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {l}
            </button>
          ))}
        </div>
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-48">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              placeholder="Buscar por nome ou telefone..."
              className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <button type="submit" className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Buscar</button>
          {search && <button type="button" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }} className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">Limpar</button>}
        </form>
        <button onClick={fetchData} className="p-2 text-gray-400 hover:text-gray-600"><RefreshCw size={16} /></button>
      </div>

      {/* Totalizador */}
      <p className="text-sm text-gray-500">{total} atendimento{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}</p>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400"><RefreshCw size={24} className="animate-spin mx-auto mb-2" />Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Users size={32} className="mx-auto mb-2 opacity-30" />
            <p>Nenhum atendimento encontrado</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Lead</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Telefone</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Última mensagem</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Atualizado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const stage = STAGE_LABEL[r.stage] || STAGE_LABEL.new;
                const lastMsg = r.last_message;
                return (
                  <tr key={r.phone} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          {r.human_takeover
                            ? <UserCircle size={16} className="text-yellow-600" />
                            : <Bot size={16} className="text-blue-600" />}
                        </div>
                        <span className="font-medium text-gray-800">{r.contact_name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-600 text-xs">{fmtPhone(r.phone)}</td>
                    <td className="px-4 py-3 max-w-xs">
                      {lastMsg ? (
                        <span className={`text-xs ${lastMsg.direction === 'in' ? 'text-gray-600' : 'text-blue-600'} truncate block max-w-[240px]`}>
                          {lastMsg.direction === 'out' && <span className="font-semibold mr-1">{bot === 'bruna' ? 'Bruna:' : 'Iago:'}</span>}
                          {lastMsg.content?.slice(0, 80)}{(lastMsg.content?.length || 0) > 80 ? '…' : ''}
                        </span>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${stage.cls}`}>{stage.label}</span>
                      {r.human_takeover && <span className="ml-1 text-xs font-semibold px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">Humano</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{fmtTs(r.updated_at)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setModalPhone(r.phone)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-medium hover:bg-blue-100"
                      >
                        <MessageSquare size={12} /> Ver conversa
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-600">Página {page} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40">
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Componente Principal ──────────────────────────────────────────────────────

export default function Automacoes() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [statsRes, configsRes, empRes] = await Promise.all([
        api.get('/automacoes/dashboard'),
        api.get('/automacoes/configs', { headers: { 'Cache-Control': 'no-cache' } }),
        api.get('/empreendimentos'),
      ]);
      setStats(statsRes.data);
      setConfigs(configsRes.data);
      setEmpreendimentos(empRes.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleSaveConfig(botKey) {
    const config = configs.find(c => c.bot_key === botKey);
    setSaving(true);
    try {
      await api.put('/automacoes/configs/' + botKey, config);
      alert('Configuração salva com sucesso!');
    } catch (err) { alert('Erro ao salvar configuração.'); }
    setSaving(false);
  }

  const handleInputChange = (botKey, field, value) => {
    setConfigs(prev => prev.map(c => c.bot_key === botKey ? { ...c, [field]: value } : c));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Automações de Atendimento</h1>
          <p className="text-gray-500">Gerencie a inteligência e monitore os bots Iago e Bruna</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <RefreshCw size={16} /> Atualizar
        </button>
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        {[
          { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          { key: 'atendimentos', icon: Users, label: 'Atendimentos IA' },
          { key: 'configs', icon: Settings2, label: 'Configurar IA' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={'px-6 py-3 text-sm font-medium border-b-2 transition-colors ' +
              (activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            <div className="flex items-center gap-2"><tab.icon size={18} /> {tab.label}</div>
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && (
        <div className="space-y-8">
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Activity className="text-blue-500" size={20} /> Iago SDR (Pré-atendimento)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard label="Total Atendimentos" value={stats?.sdr?.total} icon={MessageSquare} color="bg-blue-50 text-blue-600" />
              <StatCard label="Em Atendimento IA" value={stats?.sdr?.active} icon={Activity} color="bg-green-50 text-green-600" />
              <StatCard label="Transferidos (Humano)" value={stats?.sdr?.human} icon={UserCheck} color="bg-purple-50 text-purple-600" />
            </div>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Activity className="text-orange-500" size={20} /> Iago Vendedor (API Meta)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard label="Total Atendimentos" value={stats?.vendedor?.total} icon={MessageSquare} color="bg-orange-50 text-orange-600" />
              <StatCard label="Em Atendimento IA" value={stats?.vendedor?.active} icon={Activity} color="bg-green-50 text-green-600" />
              <StatCard label="Transferidos (Humano)" value={stats?.vendedor?.human} icon={UserCheck} color="bg-purple-50 text-purple-600" />
            </div>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Activity className="text-pink-500" size={20} /> Bruna SDR (Pós-ligação — Agendamento)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatCard label="Total Atendimentos" value={stats?.bruna?.total} icon={MessageSquare} color="bg-pink-50 text-pink-600" />
              <StatCard label="Aguardando Visita" value={stats?.bruna?.active} icon={Activity} color="bg-green-50 text-green-600" />
              <StatCard label="Visitas Agendadas" value={stats?.bruna?.visitas} icon={UserCheck} color="bg-purple-50 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'atendimentos' && <AtendimentosTab />}

      {activeTab === 'configs' && (
        <div className="space-y-8">

          {/* Cards por bot: apenas prompt e script */}
          {configs.map(bot => (
            <div key={bot.bot_key} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-bold text-gray-800 text-lg">{bot.name}</h3>
                <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-1 rounded">{bot.bot_key}</span>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">A Mente (System Prompt)</label>
                  <p className="text-xs text-gray-500 mb-2">Personalidade, objetivos e regras de comportamento do bot.</p>
                  <textarea
                    value={bot.system_prompt || ''}
                    onChange={e => handleInputChange(bot.bot_key, 'system_prompt', e.target.value)}
                    rows={10}
                    className="w-full p-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                    placeholder="Ex: Você é um consultor empático..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Roteiro / Script de Atendimento</label>
                  <p className="text-xs text-gray-500 mb-2">Instruções rápidas ou fluxo lógico que o bot deve seguir.</p>
                  <textarea
                    value={bot.script || ''}
                    onChange={e => handleInputChange(bot.bot_key, 'script', e.target.value)}
                    rows={6}
                    className="w-full p-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Ex: 1. Perguntar nome; 2. Qualificar interesse..."
                  />
                </div>
                <div className="flex justify-end">
                  <button onClick={() => handleSaveConfig(bot.bot_key)} disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 font-medium disabled:opacity-50">
                    <Save size={18} /> {saving ? 'Salvando...' : 'Salvar Script e Prompt'}
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Base de conhecimento por bot — reaproveita BotKnowledgeBase.jsx pros 3 bots */}
          {[
            { key: 'iago-sdr', title: 'Iago SDR', accent: 'orange', description: 'Materiais, localizações e contatos usados pelo Iago SDR e Iago Vendedor' },
            { key: 'bruna-sdr', title: 'Bruna SDR', accent: 'pink', description: 'Materiais, localizações e contatos usados pela Bruna SDR (pós-ligação, agendamento de visitas)' },
            { key: 'parceiros', title: 'Parceiros', accent: 'blue', description: 'Materiais, localizações e contatos usados pelo copiloto de WhatsApp de corretores parceiros' },
          ].map(({ key, title, accent, description }) => {
            const cfg = configs.find(c => c.bot_key === key);
            if (!cfg) return null;
            return (
              <BotKnowledgeBase
                key={key}
                botKey={key}
                title={title}
                accentColor={accent}
                description={description}
                materiais={cfg.materiais}
                localizacoes={cfg.localizacoes}
                contatos={cfg.contatos}
                empreendimentos={empreendimentos}
                onChanged={fetchData}
              />
            );
          })}

        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
      <div className={'p-3 rounded-lg ' + color}><Icon size={24} /></div>
      <div>
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800">{value || 0}</p>
      </div>
    </div>
  );
}
